import API from './api'
import { MerchantController } from './controllers/global';
import { clearAgendaLoginAlertSession } from './controllers/global/AgendaAlert';
import { useMerchant } from "../store/global/useMerchant";

export const TOKEN_KEY = '@petland'
export const IMPERSONATION_KEY = '@petland:impersonation'

const SESSION_STORAGE_KEYS = [
  TOKEN_KEY,
  'STORE',
  'userName',
  'modules',
  'type',
  'VET',
  'theme',
  'documentAssAlert',
  'employee',
  'role',
]

export const getToken = () => localStorage.getItem(TOKEN_KEY)
export const isAuthenticated = () => getToken() !== null

export const getSessionTheme = (modules = false, vetUser = false) => {
  if (!modules) return false

  const hasBathSchedule = !!modules?.bath?.schedule
  const hasClinicSchedule = !!modules?.clinic?.schedule

  if (vetUser === 'Veterinário' || (!hasBathSchedule && hasClinicSchedule)) {
    return 'draMei'
  }

  return 'petland'
}

export const getSessionHomePath = () => {
  try {
    if (isAdminSession() && !isImpersonating()) return '/admin'

    const modules = localStorage.getItem('modules')
    const vetUser = localStorage.getItem('VET')
    const parsedModules = modules ? JSON.parse(modules) : false
    const theme = getSessionTheme(parsedModules, vetUser)

    return theme === 'petland' ? '/hub' : '/agenda'
  } catch {
    return '/agenda'
  }
}

export const getImpersonationSession = () => {
  try {
    const session = localStorage.getItem(IMPERSONATION_KEY)
    return session ? JSON.parse(session) : false
  } catch {
    localStorage.removeItem(IMPERSONATION_KEY)
    return false
  }
}

export const isImpersonating = () => {
  const session = getImpersonationSession()
  return !!session?.active
}

export const isAdminSession = () => localStorage.getItem('type') === 'admin'

export const canUseImpersonation = () => {
  if (isImpersonating()) return false

  return isAdminSession()
}

const readCurrentSession = () =>
  SESSION_STORAGE_KEYS.reduce((session, key) => {
    session[key] = localStorage.getItem(key)
    return session
  }, {})

const restoreSessionValues = (session = {}) => {
  SESSION_STORAGE_KEYS.forEach((key) => {
    const value = session[key]

    if (value === null || value === undefined) {
      localStorage.removeItem(key)
      return
    }

    localStorage.setItem(key, value)
  })
}

const setImpersonationAllowed = (data = {}, userInfo = {}) => {
  const allowed =
    data?.canImpersonate ||
    data?.impersonation?.enabled ||
    data?.impersonation?.allowed ||
    userInfo?.canImpersonate ||
    userInfo?.permissions?.impersonation

  if (allowed) localStorage.setItem('impersonationAllowed', 'true')
}

export const applySessionPayload = (data = {}) => {
  const token = data.token
  const userInfo = data.userInfo || data.user || false
  const store = data.store || false
  const modules = userInfo?.modules || data.modules || false
  const type = userInfo?.type || data.type || false
  const employeeType = userInfo?.employeeType || data.employeeType || false
  const employeeId =
    userInfo?.employeeData?.employeeId ||
    userInfo?.employeeId ||
    userInfo?.userId ||
    false

  localStorage.setItem("documentAssAlert", false);

  if (!!token) localStorage.setItem(TOKEN_KEY, token);
  if (!!userInfo && userInfo.name) localStorage.setItem("userName", userInfo.name);
  if (!!userInfo && !userInfo.name && userInfo.email) localStorage.setItem("userName", userInfo.email);
  if (!!userInfo && userInfo.store) localStorage.setItem("STORE", userInfo.store);
  if (!!store && (store.name || store.storeName)) localStorage.setItem("STORE", store.name || store.storeName);
  if (!!modules) localStorage.setItem("modules", JSON.stringify(modules));
  if (!!type) localStorage.setItem("type", type);
  if (!!employeeType) localStorage.setItem("VET", employeeType);

  const theme = getSessionTheme(modules, employeeType)
  if (theme) localStorage.setItem('theme', theme)

  if (!!userInfo && employeeId) {
    localStorage.setItem('employee', employeeId)
    localStorage.removeItem('role')
  }

  if (!!userInfo && !employeeId) {
    localStorage.removeItem('employee')
    localStorage.setItem('role', 'master')
  }

  setImpersonationAllowed(data, userInfo)
}

export const startImpersonationSession = (data = {}) => {
  const originalSession = getImpersonationSession()?.originalSession || readCurrentSession()

  applySessionPayload(data)

  const session = {
    active: true,
    originalSession,
    impersonation: {
      ...(data.impersonation || {}),
      targetUser: data.user || data.userInfo || data.impersonation?.targetUser,
      store: data.store || data.impersonation?.store,
      startedAt: data.impersonation?.startedAt || new Date().toISOString(),
    },
  }

  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session))
  window.dispatchEvent(new Event('storage'))
}

export const restoreOriginalSession = () => {
  const session = getImpersonationSession()

  if (!session?.originalSession?.[TOKEN_KEY]) return false

  restoreSessionValues(session.originalSession)
  localStorage.removeItem(IMPERSONATION_KEY)
  window.dispatchEvent(new Event('storage'))

  return true
}

function clearUser() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('STORE')
  localStorage.removeItem('userName')
  localStorage.removeItem('modules')
  localStorage.removeItem('type')
  localStorage.removeItem('VET')
  localStorage.removeItem('theme')
  localStorage.removeItem('documentAssAlert')
  localStorage.removeItem('employee')
  localStorage.removeItem('role')
  localStorage.removeItem('impersonationAllowed')
  localStorage.removeItem(IMPERSONATION_KEY)
  localStorage.removeItem('@AgendaPetland-tables')
  clearAgendaLoginAlertSession()

  useMerchant.getState().actions.clear()

  window.dispatchEvent(new Event('storage'))

}


export const logout = () => {
  clearUser()
  return true
}

export const login = async (email, password, setLoginClik) => {
  const CMerchant = MerchantController();

  setLoginClik(true)
  clearUser()

  try {
    const { data } = await API.post('user/auth', {
      email,
      password,
    })

    CMerchant.load();

    applySessionPayload(data)
    window.dispatchEvent(new Event('storage'))

    setLoginClik(false)
  } catch (err) {

    const error = err.response.data?.error || false
    const friendlyMessage = err.response.data?.friendly_message || 'Erro ao tentar logar no sistema'

    clearUser()
    setLoginClik(false)

    if (!!error) return friendlyMessage
  }

  return true;
}
